import { Ban, Car, Plus } from 'lucide-react';
import { localeUI } from '../lib/format';
import { COLORS, ordenarInspectores } from './SchedulingDayGrid';

/**
 * DayBoard del modal de agendamiento (port de ClaimRoute DayBoard.tsx): eje horario +
 * una columna por inspector, con las citas existentes, las VENTANAS VERDES de disponibilidad
 * (con clima por hora + buffers de drive-time in/out), bloque "no cabe", y el GHOST del slot
 * elegido con sus bookends de manejo. Click en una ventana libre → onPick(inspector, hora).
 * Trabaja por NOMBRE de inspector (nuestros datos no siempre tienen user id).
 */
export type BoardAppt = {
  id: string; inspector_name: string | null; scheduled_start: string; scheduled_end: string | null;
  type?: string; client_name?: string | null; address?: string | null; lat?: number | null; lng?: number | null;
};
export type Forecast = { startMs: number; endMs: number; icon: string; short: string };
export type Proposed = { startMs: number; endMs: number; lat: number | null; lng: number | null; inspectorName: string };

const DAY_START = 7, DAY_END = 20, HOUR_PX = 52;
const HOURS = Array.from({ length: DAY_END - DAY_START }, (_, i) => DAY_START + i);
const MIN_BUFFER_PX = 16;
const R_MI = 3958.8;
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const hourFrac = (ms: number) => { const d = new Date(ms); return d.getHours() + d.getMinutes() / 60; };
const clock = (ms: number) => new Date(ms).toLocaleTimeString(localeUI(), { hour: 'numeric', minute: '2-digit' });
const fmtHour = (h: number) => { const d = new Date(); d.setHours(h, 0, 0, 0); return d.toLocaleTimeString(localeUI(), { hour: 'numeric' }); };
const initials = (n: string) => n.split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase();

function haversineMiles(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat), dLng = toRad(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return R_MI * 2 * Math.asin(Math.min(1, Math.sqrt(s)));
}
function eventBox(startMs: number, endMs: number) {
  const top = Math.max(0, (hourFrac(startMs) - DAY_START) * HOUR_PX);
  const height = Math.max(46, (Math.min(hourFrac(endMs), DAY_END) - Math.max(hourFrac(startMs), DAY_START)) * HOUR_PX);
  return { top, height };
}
function bufferBox(startMs: number, endMs: number, grow: 'up' | 'down') {
  let top = (hourFrac(startMs) - DAY_START) * HOUR_PX;
  let height = (hourFrac(endMs) - hourFrac(startMs)) * HOUR_PX;
  if (height < MIN_BUFFER_PX) { if (grow === 'up') top -= MIN_BUFFER_PX - height; height = MIN_BUFFER_PX; }
  return { top: Math.max(0, top), height };
}
const fmtMins = (m: number) => (m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m`.replace(' 0m', '') : `${m}m`);
const APPT_TYPE_SHORT: Record<string, string> = {
  inspection: 'INSPECTION', adjuster_meeting: 'INSPECTION', atr: 'ATR', atr_on_site: 'ATR ON SITE', photos: 'PHOTOS', check_pickup: 'CHECK',
  material_sample: 'MATERIAL', repair_video: 'VIDEO', custom: 'CUSTOM',
};

// Empaqueta citas solapadas en "carriles" (como el DayBoard de ClaimRoute) para
// que dos visitas a la misma hora no se pinten una encima de la otra.
function packLanes<T extends { start: number; end: number }>(items: T[]): Array<T & { lane: number; lanes: number }> {
  const sorted = [...items].sort((a, b) => a.start - b.start);
  const laneEnds: number[] = [];
  const withLane = sorted.map((it) => {
    let lane = laneEnds.findIndex((end) => end <= it.start);
    if (lane === -1) { lane = laneEnds.length; laneEnds.push(it.end); } else { laneEnds[lane] = it.end; }
    return { ...it, lane };
  });
  const lanes = Math.max(1, laneEnds.length);
  return withLane.map((it) => ({ ...it, lanes }));
}

export function ScheduleDayBoard({ day, inspectors, appts, scheduleClaim, proposed, forecast, avgSpeedMph = 30, onPick }: {
  day: Date; inspectors: string[]; appts: BoardAppt[];
  scheduleClaim: { lat: number; lng: number; durationMin: number } | null;
  proposed: Proposed | null; forecast: Forecast[]; avgSpeedMph?: number;
  onPick: (inspectorName: string, start: Date) => void;
}) {
  const dayList = appts.filter((a) => a.scheduled_start && iso(new Date(a.scheduled_start)) === iso(day));
  // Mismo orden que el calendario: los que tienen visitas ese día primero; el que no tiene ninguna, al final.
  const columnas = ordenarInspectores(inspectors, (n) => dayList.filter((a) => (a.inspector_name || '') === n).length);
  const totalHeight = HOURS.length * HOUR_PX;
  const dayStartMs = (() => { const d = new Date(day); d.setHours(DAY_START, 0, 0, 0); return d.getTime(); })();
  const dayEndMs = (() => { const d = new Date(day); d.setHours(DAY_END, 0, 0, 0); return d.getTime(); })();
  const colorIdx = (n: string) => Math.max(0, inspectors.indexOf(n)) % COLORS.length;

  // ×1.3 = factor de carretera, el MISMO que usa el motor (mockLeg) — antes este board usaba
  // haversine crudo y mostraba drive-times menores que los del engine (audit 2026-07-30).
  const ROAD = 1.3;
  const driveMinTo = (claim: { lat: number; lng: number }, loc?: { lat: number | null; lng: number | null } | null) =>
    loc && loc.lat != null && loc.lng != null ? ((haversineMiles(claim, { lat: loc.lat, lng: loc.lng }) * ROAD) / Math.max(5, avgSpeedMph)) * 60 : null;
  const milesTo = (claim: { lat: number; lng: number }, loc?: { lat: number | null; lng: number | null } | null) =>
    loc && loc.lat != null && loc.lng != null ? haversineMiles(claim, { lat: loc.lat, lng: loc.lng }) * ROAD : 0;
  const weatherForWindow = (startMs: number) => forecast.find((p) => p.startMs <= startMs && startMs < p.endMs) ?? null;

  // Hora (snap 30min) a partir de la Y del click dentro de una columna.
  function timeFromY(rect: DOMRect, clientY: number): Date {
    const offsetY = Math.max(0, Math.min(clientY - rect.top, rect.height));
    const snapped = Math.round(((offsetY / HOUR_PX) * 60) / 30) * 30;
    const totalMin = DAY_START * 60 + snapped;
    const d = new Date(day); d.setHours(Math.floor(totalMin / 60), totalMin % 60, 0, 0);
    return d;
  }

  const mineOf = (name: string) => dayList.filter((a) => (a.inspector_name || '') === name)
    .map((a) => ({ start: +new Date(a.scheduled_start), end: a.scheduled_end ? +new Date(a.scheduled_end) : +new Date(a.scheduled_start) + 3600000, lat: a.lat ?? null, lng: a.lng ?? null }))
    .sort((x, y) => x.start - y.start);

  function availabilityBlocks(name: string): React.ReactNode {
    if (!scheduleClaim) return null;
    const claim = { lat: scheduleClaim.lat, lng: scheduleClaim.lng };
    const durMs = scheduleClaim.durationMin * 60000;
    const mine = mineOf(name);
    type Seg = { prev: (typeof mine)[number] | null; next: (typeof mine)[number] | null; from: number; to: number };
    const segs: Seg[] = [];
    if (mine.length === 0) segs.push({ prev: null, next: null, from: dayStartMs, to: dayEndMs });
    else {
      segs.push({ prev: null, next: mine[0]!, from: dayStartMs, to: mine[0]!.start });
      for (let i = 0; i < mine.length - 1; i++) segs.push({ prev: mine[i]!, next: mine[i + 1]!, from: mine[i]!.end, to: mine[i + 1]!.start });
      segs.push({ prev: mine[mine.length - 1]!, next: null, from: mine[mine.length - 1]!.end, to: dayEndMs });
    }
    const nodes: React.ReactNode[] = [];
    segs.forEach((seg, si) => {
      if (seg.to <= seg.from) return;
      const driveIn = driveMinTo(claim, seg.prev);
      const driveOut = driveMinTo(claim, seg.next);
      const greenStart = seg.from + (driveIn ?? 0) * 60000;
      const greenEnd = seg.to - (driveOut ?? 0) * 60000;
      if (greenEnd - greenStart < durMs) {
        const r = eventBox(seg.from, seg.to);
        nodes.push(<div key={`no${si}`} className="db-nofit" style={{ top: r.top, height: r.height }} onClick={(e) => e.stopPropagation()}><Ban size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> Won’t fit</div>);
        return;
      }
      if (driveIn != null && greenStart > seg.from) {
        const b = bufferBox(seg.from, greenStart, 'down');
        nodes.push(<div key={`in${si}`} className="db-drive in" style={{ top: b.top, height: b.height }} onClick={(e) => e.stopPropagation()}><Car size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {milesTo(claim, seg.prev).toFixed(0)}mi · {Math.round(driveIn)}min</div>);
      }
      const g = eventBox(greenStart, greenEnd);
      const wx = weatherForWindow(greenStart);
      const availMin = Math.round((greenEnd - greenStart) / 60000);
      nodes.push(
        <div key={`free${si}`} className="db-free" style={{ top: g.top, height: g.height }} title="Click to schedule here"
          onClick={(e) => { e.stopPropagation(); const col = e.currentTarget.parentElement; if (col) onPick(name, timeFromY(col.getBoundingClientRect(), e.clientY)); }}>
          <span className="db-free-label">{wx ? `${wx.icon} ` : ''}{fmtMins(availMin)} free</span>
        </div>,
      );
      if (driveOut != null && seg.to > greenEnd) {
        const b = bufferBox(greenEnd, seg.to, 'up');
        nodes.push(<div key={`out${si}`} className="db-drive out" style={{ top: b.top, height: b.height }} onClick={(e) => e.stopPropagation()}><Car size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {milesTo(claim, seg.next).toFixed(0)}mi · {Math.round(driveOut)}min</div>);
      }
    });
    return nodes;
  }

  function chosenBlocks(name: string): React.ReactNode {
    if (!proposed || proposed.lat == null || proposed.lng == null || proposed.inspectorName !== name) return null;
    const claim = { lat: proposed.lat, lng: proposed.lng };
    const mine = mineOf(name);
    const before = mine.filter((a) => a.end <= proposed.startMs).sort((x, y) => y.end - x.end)[0];
    const after = mine.filter((a) => a.start >= proposed.endMs).sort((x, y) => x.start - y.start)[0];
    const overlap = mine.some((a) => a.start < proposed.endMs && a.end > proposed.startMs);
    const driveInMin = driveMinTo(claim, before);
    const driveOutMin = driveMinTo(claim, after);
    const lateArrive = before != null && driveInMin != null && before.end + driveInMin * 60000 > proposed.startMs;
    const lateNext = after != null && driveOutMin != null && proposed.endMs + driveOutMin * 60000 > after.start;
    const ghost = eventBox(proposed.startMs, proposed.endMs);
    const nodes: React.ReactNode[] = [];
    if (driveInMin != null) {
      const b = bufferBox(proposed.startMs - driveInMin * 60000, proposed.startMs, 'up');
      nodes.push(<div key="cin" className={`db-drive in chosen ${lateArrive ? 'bad' : ''}`} style={{ top: b.top, height: b.height }}><Car size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {milesTo(claim, before).toFixed(0)}mi · {Math.round(driveInMin)}min{lateArrive ? ' · won’t make it' : ''}</div>);
    }
    nodes.push(
      <div key="cghost" className={`db-ghost ${overlap ? 'bad' : ''}`} style={{ top: ghost.top, height: ghost.height }}>
        <div className="db-ghost-head">{overlap ? <><Ban size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> Conflicts</> : <><Plus size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> Chosen</>} {clock(proposed.startMs)}</div>
      </div>,
    );
    if (driveOutMin != null) {
      const b = bufferBox(proposed.endMs, proposed.endMs + driveOutMin * 60000, 'down');
      nodes.push(<div key="cout" className={`db-drive out chosen ${lateNext ? 'bad' : ''}`} style={{ top: b.top, height: b.height }}><Car size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {milesTo(claim, after).toFixed(0)}mi · {Math.round(driveOutMin)}min{lateNext ? ' · won’t make next' : ''}</div>);
    }
    return nodes;
  }

  return (
    <div className="db">
      <div className="db-inner" style={{ gridTemplateColumns: `48px repeat(${columnas.length || 1}, minmax(150px, 1fr))` }}>
        <div className="db-corner" />
        {columnas.map((name) => {
          const c = COLORS[colorIdx(name)]!;
          const cnt = dayList.filter((a) => (a.inspector_name || '') === name).length;
          const hi = proposed?.inspectorName === name;
          return <div key={name} className={`db-colhead${hi ? ' hi' : ''}`} style={{ borderTopColor: c.ink }}><span className="db-av" style={{ background: c.ink }}>{initials(name)}</span><span className="db-name">{name}</span><span className="db-count" style={{ background: c.bg, color: c.ink }}>{cnt}</span></div>;
        })}
        <div className="db-gutter" style={{ height: totalHeight }}>
          {HOURS.map((h) => <div key={h} className="db-hourlabel" style={{ height: HOUR_PX }}>{fmtHour(h)}</div>)}
        </div>
        {columnas.map((name) => {
          const c = COLORS[colorIdx(name)]!;
          const hi = proposed?.inspectorName === name;
          const items = packLanes(
            dayList.filter((a) => (a.inspector_name || '') === name).map((a) => {
              const start = +new Date(a.scheduled_start);
              return { start, end: a.scheduled_end ? +new Date(a.scheduled_end) : start + 3600000, appt: a };
            }),
          );
          return (
            <div key={name} className={`db-col pickable${hi ? ' hi' : ''}`} style={{ height: totalHeight }}
              onClick={(e) => onPick(name, timeFromY(e.currentTarget.getBoundingClientRect(), e.clientY))}>
              {HOURS.map((h) => <div key={h} className="db-slot" style={{ height: HOUR_PX }} />)}
              {proposed?.inspectorName === name ? chosenBlocks(name) : availabilityBlocks(name)}
              {items.map((it) => {
                const { top, height } = eventBox(it.start, it.end);
                const w = 100 / it.lanes;
                const addr = it.appt.address;
                return (
                  <div key={it.appt.id} className="db-ev"
                    style={{ top, height, left: `calc(${it.lane * w}% + 3px)`, right: 'auto', width: `calc(${w}% - 6px)`, background: c.bg, color: c.ink, borderLeftColor: c.ink }}
                    onClick={(e) => e.stopPropagation()}>
                    <div className="db-ev-time">
                      <span>{clock(it.start)}</span>
                      {it.appt.type && <span className="db-ev-type">{APPT_TYPE_SHORT[it.appt.type] ?? it.appt.type}</span>}
                    </div>
                    <div className="db-ev-client">{it.appt.client_name || addr?.split(',')[0] || 'Visit'}</div>
                    {addr && <div className="db-ev-sub">{addr}</div>}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
